import React, { useState, useEffect, useCallback, useRef } from "react";
import ShareTwoToneIcon from "@mui/icons-material/ShareTwoTone";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import NotificationsIcon from "@mui/icons-material/Notifications";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Dialog,
  IconButton,
  Link,
  Snackbar,
  Alert,
  CardMedia,
  Avatar,
  Button,
  CircularProgress,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { CREATE_RECORD, CLEAR_CREATE_RECORD } from "../../redux/records/types";
import {
  GET_NEWS,
  SET_CACHED_NEWS,
  SET_NEWS_ERROR,
  Story,
} from "../../redux/home/type";
import { RootState } from "../../reducer";
import { ActionStatus } from "../../constants/api";
import {
  addRecord,
  deleteRecord,
  getAllRecords,
  initDB,
} from "../../utils/indexedDB";
import CameraNative from "../../component/Camera";
import { useNavigate } from "react-router";
import { CLEAR_TOKEN } from "../../redux/login/type";
import { compressImage, getBase64SizeKB } from "../../utils/imageCompression";

const CrypticPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux selectors for news
  const news = useSelector<RootState, Story[]>((state) => state.home.news);
  const newsLoading = useSelector<RootState, boolean>(
    (state) => state.home.loading
  );
  const newsError = useSelector<RootState, string | null>(
    (state) => state.home.error
  );
  const newsCached = useSelector<RootState, boolean>(
    (state) => state.home.cached
  );
  const NEWS_STATUS = useSelector<RootState, ActionStatus>(
    (state) => state.home.NEWS_STATUS
  );

  // Local state
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error" | "warning" | "info">("success");
  const [onlineCount, setOnlineCount] = useState(() => {
    const storedCount = localStorage.getItem("onlineRecordCount");
    return storedCount ? parseInt(storedCount, 10) : 0;
  });
  const [offlineCount, setOfflineCount] = useState(() => {
    const storedCount = localStorage.getItem("offlineRecordCount");
    return storedCount ? parseInt(storedCount, 10) : 0;
  });

  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [isCompressing, setIsCompressing] = useState<boolean>(false);
  const syncQueueRef = useRef<any[]>([]);
  const syncCountRef = useRef<number>(0);
  const [retailerName, setRetailer] = useState<string>("");
  const [category, setCategory] = useState<string>("");
  const [cameraOpen, setCameraOpen] = useState<boolean>(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [cameraKey, setCameraKey] = useState(0);

  const dbRef = useRef<IDBDatabase | null>(null);

  const [currentPosition, setCurrentPosition] = useState<{
    lat: number | null;
    long: number | null;
  }>({ lat: null, long: null });

  const CREATE_RECORD_STATUS: string = useSelector<RootState, ActionStatus>(
    (state) => state.record.CREATE_RECORD_STATUS
  );

  const showMessage = (message: string, severity: "success" | "error" | "warning" | "info" = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const fetchNews = useCallback(
    async (forceRefresh = false) => {
      try {
        if (navigator.onLine) {
          dispatch(GET_NEWS.action());
        } else {
          const cachedStories = localStorage.getItem("cachedStories");
          if (cachedStories) {
            const parsedStories = JSON.parse(cachedStories);
            dispatch(SET_CACHED_NEWS.action(parsedStories));
            dispatch(SET_NEWS_ERROR.action("Offline - showing cached news"));
          } else {
            dispatch(
              SET_NEWS_ERROR.action(
                "No internet connection and no cached news available"
              )
            );
          }
        }
      } catch (error) {
        console.error("Error fetching news:", error);
        const cachedStories = localStorage.getItem("cachedStories");
        if (cachedStories) {
          const parsedStories = JSON.parse(cachedStories);
          dispatch(SET_CACHED_NEWS.action(parsedStories));
          dispatch(
            SET_NEWS_ERROR.action(
              "Failed to fetch fresh news - showing cached news"
            )
          );
        } else {
          dispatch(
            SET_NEWS_ERROR.action(
              "Failed to fetch news and no cached news available"
            )
          );
        }
      }
    },
    [dispatch]
  );

  useEffect(() => {
    const initializeDB = async () => {
      try {
        const db = await initDB();
        dbRef.current = db;
      } catch (error) {
        console.error("Failed to initialize IndexedDB:", error);
      }
    };

    initializeDB();

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    const storedRetailer = localStorage.getItem("selectedRetailer") || "";
    const storedCategory = localStorage.getItem("selectedCategory") || "";
    setRetailer(storedRetailer);
    setCategory(storedCategory);

    fetchNews();

    if ("geolocation" in navigator) {
      const watchId = navigator.geolocation.watchPosition(
        (position) => {
          setCurrentPosition({
            lat: position.coords.latitude,
            long: position.coords.longitude,
          });
        },
        (error) => {
          console.error("Error getting location:", error);
          setCurrentPosition({ lat: null, long: null });
        },
        { enableHighAccuracy: true, timeout: 20000, maximumAge: 1000 }
      );

      return () => {
        window.removeEventListener("online", handleOnline);
        window.removeEventListener("offline", handleOffline);
        navigator.geolocation.clearWatch(watchId);
      };
    }
  }, [fetchNews]);

  useEffect(() => {
    if (NEWS_STATUS === ActionStatus.SUCCESS && !newsLoading) {
      if (newsCached) {
        showMessage("Showing cached news", "info");
      } else {
        showMessage("News updated successfully");
        localStorage.setItem("cachedStories", JSON.stringify(news));
        localStorage.setItem("storiesCacheTime", Date.now().toString());
      }
    } else if (NEWS_STATUS === ActionStatus.FAILURE) {
      const cachedStories = localStorage.getItem("cachedStories");
      if (cachedStories && news.length === 0) {
        const parsedStories = JSON.parse(cachedStories);
        dispatch(SET_CACHED_NEWS.action(parsedStories));
        showMessage("Failed to load fresh news - showing cached news", "warning");
      } else {
        showMessage("Failed to load news", "error");
      }
    }
  }, [NEWS_STATUS, newsLoading, newsError, newsCached, news, dispatch]);

  useEffect(() => {
    if (!isOnline) {
      showMessage("You are offline.", "warning");
    }
  }, [isOnline]);

  const handleOnline = useCallback(() => {
    setIsOnline(true);
    syncOfflineRecords();
    if (news.length === 0) {
      fetchNews();
    }
  }, [fetchNews, news.length]);

  const handleOffline = () => {
    setIsOnline(false);
  };

  useEffect(() => {
    localStorage.setItem("onlineRecordCount", onlineCount.toString());
  }, [onlineCount]);

  useEffect(() => {
    localStorage.setItem("offlineRecordCount", offlineCount.toString());
  }, [offlineCount]);

  const syncOfflineRecords = useCallback(async () => {
    if (!dbRef.current || isSyncing || isCameraOpen) {
      console.log("Syncing prevented:", { isSyncing, isCameraOpen });
      return;
    }

    setIsSyncing(true);

    try {
      const offlineRecords = await getAllRecords(dbRef.current);
      console.log("Syncing offline records:", offlineRecords);

      syncQueueRef.current = [...offlineRecords];
      processNextInQueue();
    } catch (error) {
      console.error("Error fetching offline records:", error);
      showMessage("Error fetching offline records. Please try again.", "error");
      setIsSyncing(false);
    }
  }, [dbRef, isSyncing, isCameraOpen]);

  const processNextInQueue = useCallback(async () => {
    if (syncQueueRef.current.length === 0 || isCameraOpen) {
      setIsSyncing(false);
      showMessage("Sync completed successfully!");
      return;
    }

    const record = syncQueueRef.current.shift();
    const { id, ...payloadWithoutId } = record;

    try {
      await dispatch(CREATE_RECORD.action(payloadWithoutId));

      if (dbRef.current) {
        await deleteRecord(dbRef.current, id);
      }

      setOfflineCount((prevCount) => {
        const newCount = Math.max(0, prevCount - 1);
        localStorage.setItem("offlineRecordCount", newCount.toString());
        return newCount;
      });

      setTimeout(processNextInQueue, 0);
    } catch (error) {
      console.error("Error syncing record:", error);
      syncQueueRef.current.unshift(record);
      setTimeout(processNextInQueue, 5000);
    }
  }, [dispatch, dbRef, isCameraOpen]);

  const handleBookmarkClick = (): void => {
    showMessage("Bookmark saved!");
  };

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ): void => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  const handleCameraClose = useCallback((): void => {
    setCameraOpen(false);
    setIsCameraOpen(false);
    setCameraReady(false);
    console.log("Camera closed, syncing resumed if needed");
  }, []);

  const handleImageCapture = useCallback(
    async (imageSrc: string) => {
      if (isSyncing) {
        showMessage("Please wait for sync to complete.", "warning");
        return;
      }

      setIsCompressing(true);

      try {
        // Get coordinates
        let coordinates: [number, number] | null = null;

        if (currentPosition.lat !== null && currentPosition.long !== null) {
          coordinates = [currentPosition.long, currentPosition.lat];
        } else if ("geolocation" in navigator) {
          try {
            const position = await new Promise<GeolocationPosition>(
              (resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, {
                  timeout: 10000,
                  maximumAge: 60000,
                });
              }
            );
            coordinates = [position.coords.longitude, position.coords.latitude];
          } catch (error) {
            if (error instanceof GeolocationPositionError) {
              if (error.code === error.PERMISSION_DENIED) {
                showMessage(
                  "Location access is disabled. Please enable location services.",
                  "error"
                );
                setIsCompressing(false);
                handleCameraClose();
                return;
              }
            }
          }
        }

        if (coordinates === null) {
          showMessage(
            "Unable to get location. Please ensure location services are enabled.",
            "error"
          );
          setIsCompressing(false);
          handleCameraClose();
          return;
        }

        // Compress the image
        const originalSize = getBase64SizeKB(imageSrc);
        console.log(`Original image size: ${originalSize.toFixed(2)} KB`);

        const compressedImage = await compressImage(imageSrc, {
          maxWidth: 1024,
          maxHeight: 1024,
          quality: 0.7,
          maxSizeKB: 400,
        });

        const base64Data = compressedImage.split(",")[1] ?? compressedImage;

        const payload = {
          retailerName,
          creationDate: new Date().toISOString(),
          categoryCode: category,
          image: base64Data,
          coordinates: coordinates,
        };

        if (dbRef.current) {
          try {
            await addRecord(dbRef.current, payload);
            setOfflineCount((prevCount) => {
              const newCount = prevCount + 1;
              localStorage.setItem("offlineRecordCount", newCount.toString());
              return newCount;
            });
            showMessage("Image stored successfully!", "success");
            console.log("Image stored in IndexedDB");
          } catch (error) {
            console.error("Error storing image:", error);
            showMessage("Error storing image. Storage may be full.", "error");
          }
        }
        setIsCompressing(false);
        handleCameraClose();
      } catch (error) {
        console.error("Error handling capture:", error);
        showMessage("Error processing image. Please try again.", "error");
        setIsCompressing(false);
        handleCameraClose();
      }
    },
    [currentPosition, retailerName, category, handleCameraClose, isSyncing]
  );

  useEffect(() => {
    const syncInterval = setInterval(() => {
      if (isOnline && offlineCount > 0 && !isSyncing && !isCameraOpen) {
        syncOfflineRecords();
      }
    }, 4000);

    return () => clearInterval(syncInterval);
  }, [isOnline, offlineCount, isSyncing, syncOfflineRecords, isCameraOpen]);

  useEffect(() => {
    if (CREATE_RECORD_STATUS === ActionStatus.SUCCESS) {
      showMessage("Success!");
      setOnlineCount((prevCount) => {
        let increment = 1;
        if (isSyncing && syncCountRef.current > 0) {
          increment = syncCountRef.current;
          syncCountRef.current = 0;
        }
        const newCount = prevCount + increment;
        localStorage.setItem("onlineRecordCount", newCount.toString());
        return newCount;
      });
      dispatch(CLEAR_CREATE_RECORD.action());
    } else if (CREATE_RECORD_STATUS === ActionStatus.FAILURE) {
      showMessage("Error. Please try again.", "error");
    }
  }, [CREATE_RECORD_STATUS, isSyncing, dispatch]);

  return (
    <Container
      maxWidth="sm"
      sx={{
        background: "linear-gradient(to right, #f7f7f7, #e0e0e0)",
        borderRadius: 2,
        boxShadow: 3,
        p: 2,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box sx={{ mb: 4, display: "flex", justifyContent: "space-around" }}>
        <Typography
          variant="h5"
          align="left"
          sx={{ fontWeight: "bold", color: "#3f51b5", cursor: "pointer" }}
          onClick={() => {
            if (!isOnline) {
              showMessage("You are offline. Cannot navigate at this time.", "warning");
            } else if (offlineCount > 0) {
              showMessage(
                "You have unsynced news. Please wait for sync to complete.",
                "warning"
              );
            } else {
              localStorage.removeItem("onlineRecordCount");
              localStorage.removeItem("offlineRecordCount");
              navigate("/home");
            }
          }}
        >
          Latest News
        </Typography>

        <IconButton onClick={() => {
          setCameraKey(prev => prev + 1);
          setCameraOpen(true);
          setIsCameraOpen(true);
          setIsSyncing(false);
          syncQueueRef.current = [];
        }}>
          <NotificationsIcon style={{ color: "black", fontSize: "18px" }} />
        </IconButton>
        <IconButton onClick={handleBookmarkClick}>
          <BookmarkBorderIcon style={{ color: "black", fontSize: "18px" }} />
        </IconButton>
        <IconButton onClick={handleBookmarkClick}>
          <ShareTwoToneIcon
            style={{
              color: "black",
              fontSize: "18px",
              marginLeft: "8px",
            }}
          />
        </IconButton>

        {!isOnline && (
          <IconButton>
            <WifiOffIcon style={{ color: "black", fontSize: "18px" }} />
          </IconButton>
        )}

        <Avatar
          src="/broken-image.jpg"
          onClick={() => {
            if (!isOnline) {
              showMessage("You are offline. Cannot logout.", "warning");
            } else if (offlineCount > 0) {
              showMessage(
                "You have unsynced news. Please wait for sync to complete.",
                "warning"
              );
            } else {
              setShowLogoutDialog(true);
            }
          }}
          sx={{ cursor: "pointer" }}
        />
      </Box>

      {/* Loading indicator */}
      {newsLoading && (
        <Box display="flex" justifyContent="center" mb={2}>
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ ml: 1 }}>
            Loading news...
          </Typography>
        </Box>
      )}

      {/* News cards */}
      {news.map((story, index) => {
        if (!story.image) return null;
        return (
          <Box key={index}>
            <Card
              sx={{
                mb: 2,
                p: 2,
                boxShadow: 3,
                backgroundColor: "#fff",
                borderRadius: 2,
                "&:hover": { boxShadow: 6 },
                transition: "box-shadow 0.3s ease",
              }}
            >
              <CardMedia
                component="img"
                height="200"
                image={story.image}
                alt={story.title}
              />

              <CardContent>
                <Box
                  display="flex"
                  alignItems="flex-start"
                  justifyContent="space-between"
                  gap="4px"
                >
                  <Typography variant="h6">
                    <Link
                      href={story.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="always"
                      color="inherit"
                    >
                      {story.title}
                    </Link>
                  </Typography>
                  <Typography variant="body2" color="black" sx={{ mt: 1 }}>
                    {onlineCount}
                  </Typography>
                </Box>
                <Box display="inline-flex" alignItems="flex-start">
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1, width: "160px" }}
                  >
                    {story.author ? `${story.author} | ` : ""}
                    {new Date(story.pubDate).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}{" "}
                    / {offlineCount}
                  </Typography>
                </Box>
                <Box display="inline-flex" alignItems="flex-start">
                  <IconButton onClick={() => {
                    setCameraKey(prev => prev + 1);
                    setCameraOpen(true);
                    setIsCameraOpen(true);
                    setIsSyncing(false);
                    syncQueueRef.current = [];
                  }}>
                    <NotificationsIcon
                      style={{ color: "black", fontSize: "18px" }}
                    />
                  </IconButton>
                  <IconButton onClick={handleBookmarkClick}>
                    <BookmarkBorderIcon
                      style={{ color: "black", fontSize: "18px" }}
                    />
                  </IconButton>
                  <IconButton onClick={handleBookmarkClick}>
                    <ShareTwoToneIcon
                      style={{
                        color: "black",
                        fontSize: "18px",
                        marginLeft: "8px",
                      }}
                    />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </Box>
        );
      })}

      {/* Camera opens automatically when cameraOpen is true */}
      {cameraOpen && (
        <CameraNative
          key={cameraKey}
          onCapture={handleImageCapture}
          onClose={handleCameraClose}
        />
      )}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Dialog
        open={showLogoutDialog}
        onClose={() => {
          setShowLogoutDialog(false);
        }}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
        fullWidth
      >
        <DialogContent>
          <DialogContentText id="logout-dialog-description">
            Are you sure?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowLogoutDialog(false);
            }}
            color="primary"
          >
            No
          </Button>
          <Button
            onClick={() => {
              dispatch(CLEAR_TOKEN.action());
              window.location.reload();
            }}
            color="primary"
            variant="contained"
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CrypticPage;